<template>
  <div class="images-widget">
    <PictureCarousel
      :key="widget.files.length"
      :file-ids="widget.files"
      class="f-h"
      :default-index="currentPictureIndex"
      :no-arrows="onlyOnePic === 1"
      :no-text="onlyOnePic === 1"
      display-pdf
      :prevent-pdf-interaction="imagesEditable"
      @changeIndex="changeIndex"
      @pictureClick="imagesEditable ? fileViewerVisible = true : ''"
    />
    <transition name="fade">
      <FileViewer
        v-show="fileViewerVisible"
        ref="fileViewer"
        v-model="widget.files"
        :index="fileViewerIndex"
        :editable="true"
        pathname="widgets/"
        @input="fileViewerInput"
        @close="fileViewerVisible = false"
      />
    </transition>
  </div>
</template>

<script>
/**
 * Images widget. Allows the user to add multiple images
 *
 * @vue-prop {Boolean} [onlyOnePic] - if true, disallow sliding pictures
 * @vue-prop {Number} selectedIndex - index of the picture to focus by default
 */

import { SET_CFG_PAGE_WIDGET } from '@/store/action-types';
import FileViewer from '@/components/file-system/file-viewer.vue';
import PictureCarousel from '@/components/picture-carousel.vue';

// Mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';

export default {

  name: 'ImagesWidget',

  components: {
    FileViewer,
    PictureCarousel,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinSocketHandler],

  props: {
    onlyOnePic: {
      type: Number,
      default: 0,
    },
    selectedIndex: {
      type: Number,
      default: 0,
    },
    imagesEditable: Boolean,
  },

  data() {
    return {
      fileViewerVisible: false,
      fileViewerIndex: 0,
    };
  },

  computed: {
    config() {
      return [
        {
          key: 'onlyOnePic',
          value: this.onlyOnePic,
          component: 'IntCheckbox',
          options: {
            class: 'mt5',
            title: this.$t('message.WIDGET_PICTS_ONLY_ONE'),
          },
        },
        {
          key: 'imagesEditable',
          value: this.imagesEditable,
          component: 'Checkbox',
          options: {
            title: this.$t('message.WIDGET_PICTS_EDITABLE'),
            class: 'block',
          },
        },
        {
          key: 'imagesEditableHint',
          value: this.imagesEditable,
          component: 'Div',
          options: {
            class: 'field-hint',
          },
          slots: [{
            component: 'Div',
            content: this.$t('message.WIDGET_PICTS_EDITABLE_HINT'),
          }],
        },
        {
          key: 'files',
          value: this.widget.files,
          component: 'AutonomousFileChipList',
          options: {
            class: 'mt5',
            pathname: 'widgets/',
            editable: true,
            column: true,
            forceButtonText: true,
          },
        },
        this.defaultBgColorConfig,
      ];
    },
    /** Number: Index of the currently selected picture */
    currentPictureIndex() {
      return this.selectedIndex % this.widget.files.length;
    },
  },

  watch: {
    fileViewerVisible() {
      this.setMaxZIndex(this.fileViewerVisible);
    },
  },

  methods: {
    /** Update the files of the widget and send socket message when the files change */
    fileViewerInput() {
      this.$store.dispatch(SET_CFG_PAGE_WIDGET, this.widget)
        .then(() => {
          this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.WIDGET, id: this.widget.id });
        });
    },
    /** Chang the selected picture's index */
    changeIndex(index) {
      this.$emit('configChange', { key: 'selectedIndex', value: index });
    },
  },
};

</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.images-widget.widget-component-child {
  height: 100%;
  overflow: hidden;

  .solo-img {
    height: 100%;

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }

  .picture-carousel__current-picture {
    height: 100%;

    img {
      max-height: 100%;
    }
  }
}
</style>
